export type CarrierType = 'UPS' | 'FedEx' | 'DHL' | 'USPS' | 'PostNL' | 'DPD' | 'TNT' | 'Aramex' | 'bpost' | 'Cainiao' | 'Unknown';

export interface CarrierInfo {
  carrier: CarrierType;
  name: string;
  trackingUrl: string;
}

export function detectCarrier(trackingCode: string): CarrierInfo {
  if (!trackingCode) {
    return {
      carrier: 'Unknown',
      name: 'Unknown Carrier',
      trackingUrl: ''
    };
  }

  const code = trackingCode.trim().toUpperCase().replace(/\s+/g, '');

  // Check Cainiao first (most specific patterns)
  // Formats: LP123456789CN, SGALN123456CN, YT1234567890123, HRBC1028124202, etc.
  if (
    /^(LP|LX|LT|LS|LM|LK|LB|LO|LY|LZ|LE|LA|LC|LN|LD|LH|LG|LF)\d{9}[A-Z]{2}$/.test(code) ||
    /^SGALN\d+CN$/.test(code) ||
    /^YT\d{13}$/.test(code) ||
    /^[A-Z]{2}\d{9}CN$/.test(code) ||
    /^\d{13}YT$/.test(code) ||
    /^[A-Z]{4}\d{10}YQ$/.test(code) ||
    /^[A-Z]{4}\d{10}$/.test(code)
  ) {
    return {
      carrier: 'Cainiao',
      name: 'Cainiao',
      trackingUrl: `https://global.cainiao.com/detail.htm?mailNoList=${code}`
    };
  }

  // Check bpost (before DHL) to avoid conflicts
  if (/^[A-Z]{2}\d{9}BE$/.test(code) || /^323\d{10}$/.test(code)) {
    return {
      carrier: 'bpost',
      name: 'bpost',
      trackingUrl: `https://track.bpost.cloud/btr/web/#/search?itemCode=${code}&lang=en`
    };
  }

  if (/^1Z[A-Z0-9]{16}$/.test(code)) {
    return {
      carrier: 'UPS',
      name: 'UPS',
      trackingUrl: `https://www.ups.com/track?tracknum=${code}`
    };
  }

  if (/^\d{12,22}$/.test(code) || /^\d{15}$/.test(code)) {
    return {
      carrier: 'FedEx',
      name: 'FedEx',
      trackingUrl: `https://www.fedex.com/fedextrack/?tracknumbers=${code}`
    };
  }

  if (/^\d{10,11}$/.test(code) || /^[A-Z]{2}\d{9}[A-Z]{2}$/.test(code)) {
    return {
      carrier: 'DHL',
      name: 'DHL',
      trackingUrl: `https://www.dhl.com/en/express/tracking.html?AWB=${code}`
    };
  }

  if (/^\d{20,22}$/.test(code) || /^94\d{20}$/.test(code) || /^92\d{20}$/.test(code) || /^93\d{20}$/.test(code)) {
    return {
      carrier: 'USPS',
      name: 'USPS',
      trackingUrl: `https://tools.usps.com/go/TrackConfirmAction?tLabels=${code}`
    };
  }

  if (/^3S[A-Z0-9]{18}$/.test(code) || /^[A-Z]{2}\d{9}NL$/.test(code) || /^\d{13}$/.test(code)) {
    return {
      carrier: 'PostNL',
      name: 'PostNL',
      trackingUrl: `https://postnl.nl/tracktrace/?B=${code}&P=&D=&T=C`
    };
  }

  if (/^\d{14}$/.test(code) || /^\d{12}$/.test(code)) {
    return {
      carrier: 'DPD',
      name: 'DPD',
      trackingUrl: `https://tracking.dpd.de/status/en_US/parcel/${code}`
    };
  }

  if (/^\d{9}$/.test(code) || /^[A-Z]{2}\d{9}$/.test(code)) {
    return {
      carrier: 'TNT',
      name: 'TNT',
      trackingUrl: `https://www.tnt.com/express/en_us/site/tracking.html?searchType=con&cons=${code}`
    };
  }

  return {
    carrier: 'Unknown',
    name: 'Unknown Carrier',
    trackingUrl: ''
  };
}

export function getCarrierColor(carrier: CarrierType): string {
  switch (carrier) {
    case 'UPS':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
    case 'FedEx':
      return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
    case 'DHL':
      return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
    case 'USPS':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
    case 'PostNL':
      return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
    case 'DPD':
      return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
    case 'TNT':
      return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
    case 'Aramex':
      return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
    case 'bpost':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
    case 'Cainiao':
      return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
  }
}

export function getTrackingStatusColor(status: string): string {
  switch (status) {
    case 'delivered':
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    case 'out_for_delivery':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
    case 'in_transit':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
    case 'pending_pickup':
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    case 'failed_delivery':
      return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
    case 'returned':
      return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
  }
}

export function formatTrackingStatus(status: string): string {
  return status
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
